import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';

// Create the context
const CourseContext = createContext();

// Create a provider component
export const CourseProvider = ({ children }) => {
  const [currentCourse, setCurrentCourse] = useState(null);

  // Use useCallback to create a stable function reference that doesn't change on every render
  const updateCourse = useCallback((course) => {
    setCurrentCourse(prevCourse => {
      // If course is null and prevCourse is null, don't update
      if (course === null && prevCourse === null) return prevCourse;
      
      // If one is null and the other isn't, update
      if ((course === null && prevCourse !== null) || 
          (course !== null && prevCourse === null)) {
        return course;
      }
      
      // If they're both objects, compare IDs
      if (course && prevCourse && course._id === prevCourse._id) {
        // If IDs match, only update if there are actual changes
        // This is a shallow comparison, might need to be deeper depending on your data structure
        return JSON.stringify(course) !== JSON.stringify(prevCourse) ? course : prevCourse;
      }
      
      // Otherwise, update the course
      return course;
    });
  }, []);

  // FIXED: Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    currentCourse,
    updateCourse
  }), [currentCourse, updateCourse]);

  return (
    <CourseContext.Provider value={contextValue}>
      {children}
    </CourseContext.Provider>
  );
};

// Create a custom hook for using the context
export const useCourse = () => {
  const context = useContext(CourseContext);
  if (!context) {
    throw new Error('useCourse must be used within a CourseProvider');
  }
  return context;
};